import { useEffect, useState, useCallback } from "react";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";

const resolveSrc = (url) => (url && url.startsWith("/")) ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

/**
 * PhotoCarousel - galeria reusable con flechas + thumbnails + lightbox
 *
 * Props:
 *  photos: string[] (URLs o data URLs)
 *  className: clases adicionales para el contenedor
 *  aspectClass: aspect ratio de la imagen principal (default: aspect-[4/3])
 *  altPrefix: prefijo del alt (default: "Foto")
 *  initialIndex: indice inicial
 *  enableLightbox: si true, click en imagen abre vista grande fullscreen (default true)
 *  showThumbnails: si true, muestra thumbnails abajo (default true)
 *  objectFit: "cover" (default, recorta) | "contain" (foto completa, letterbox)
 */
export function PhotoCarousel({ photos = [], className = "", aspectClass = "aspect-[4/3]", altPrefix = "Foto", initialIndex = 0, enableLightbox = true, showThumbnails = true, objectFit = "cover" }) {
  const valid = (photos || []).filter(Boolean);
  const [idx, setIdx] = useState(Math.min(initialIndex, Math.max(0, valid.length - 1)));
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (idx >= valid.length) setIdx(0);
  }, [valid.length, idx]);

  const next = useCallback(() => setIdx(i => (i + 1) % valid.length), [valid.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + valid.length) % valid.length), [valid.length]);

  // Keyboard navigation when lightbox is open
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") setLightbox(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, next, prev]);

  if (valid.length === 0) return null;

  const current = valid[idx];

  return (
    <div className={className} data-testid="photo-carousel">
      {/* Imagen principal */}
      <div className={`relative ${aspectClass} bg-[#1A1A1A] overflow-hidden group`}>
        <img
          src={resolveSrc(current)}
          alt={`${altPrefix} ${idx + 1}`}
          className={`w-full h-full ${objectFit === "contain" ? "object-contain" : "object-cover"} ${enableLightbox ? "cursor-zoom-in" : ""}`}
          onClick={() => enableLightbox && setLightbox(true)}
          data-testid="carousel-main-image"
        />
        {valid.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Foto anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/85 text-white w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-80 hover:opacity-100"
              data-testid="carousel-prev"
            ><CaretLeft size={20} weight="bold" /></button>
            <button
              type="button"
              onClick={next}
              aria-label="Foto siguiente"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/85 text-white w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-80 hover:opacity-100"
              data-testid="carousel-next"
            ><CaretRight size={20} weight="bold" /></button>
            <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full font-mono" data-testid="carousel-counter">
              {idx + 1} / {valid.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {showThumbnails && valid.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1" data-testid="carousel-thumbnails">
          {valid.map((src, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              type="button"
              aria-label={`Ver foto ${i + 1}`}
              className={`flex-shrink-0 w-16 h-16 border-2 transition-colors overflow-hidden ${i === idx ? "border-[#FF4D00]" : "border-white/10 hover:border-white/30"}`}
              data-testid={`carousel-thumb-${i}`}
            >
              <img src={resolveSrc(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox fullscreen */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center" data-testid="carousel-lightbox" onClick={() => setLightbox(false)}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-4 right-4 z-10 w-11 h-11 bg-black/60 hover:bg-black/90 text-white rounded-full flex items-center justify-center"
            aria-label="Cerrar"
            data-testid="carousel-lightbox-close"
          ><X size={22} weight="bold" /></button>
          <div className="relative w-full h-full flex items-center justify-center px-4 sm:px-12" onClick={(e) => e.stopPropagation()}>
            <img src={resolveSrc(current)} alt={`${altPrefix} ${idx + 1}`} className="max-w-full max-h-[88vh] object-contain" data-testid="carousel-lightbox-image" />
            {valid.length > 1 && (
              <>
                <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur text-white w-12 h-12 rounded-full flex items-center justify-center"><CaretLeft size={26} weight="bold" /></button>
                <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur text-white w-12 h-12 rounded-full flex items-center justify-center"><CaretRight size={26} weight="bold" /></button>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1 rounded-full font-mono">{idx + 1} / {valid.length}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoCarousel;
